import { Crown, Flame, Medal } from "lucide-react"
import { useTranslation } from "react-i18next"

import { MomentumRingAvatar } from "@/components/rank/momentum-ring-avatar"
import { formatXpNumber } from "@/lib/format-xp"
import { cn } from "@/lib/utils"
import type { CreatorLeaderboardRow } from "@/types/creator-leaderboard"

/**
 * OKS-519 — §13 top-3 podium.
 *
 * Renders ONLY when 3 rows are supplied (the page passes `[]` for the
 * <3-ranked flat-list edge). Visual layout is `#2 · #1 · #3` with #1
 * centred + elevated and a crown; #2/#3 flank with silver/bronze medals.
 * DOM order is `#1, #2, #3` (CSS `order-*` flips the visual order) so
 * screen readers announce the winner first (UX Spec §6).
 *
 * Each tile composes the OKS-512 `MomentumRingAvatar` (progress ring +
 * rank-patch corner) with name · named rank · weekly XP · 🔥 streak. The
 * viewer's own podium spot is highlighted via `viewerMembershipId`.
 */
interface LeaderboardPodiumProps {
  /** Exactly the top-3 rows (ranks 1-3). */
  podium: CreatorLeaderboardRow[]
  /** The viewer's membership id — highlights their spot when on the podium. */
  viewerMembershipId?: number | null
}

export function LeaderboardPodium({
  podium,
  viewerMembershipId,
}: LeaderboardPodiumProps) {
  if (podium.length < 3) return null

  const [first, second, third] = podium

  return (
    <section
      data-testid="creator-leaderboard-podium"
      aria-label="Podium"
      className="flex items-end justify-center gap-2 pt-2 pb-1 sm:gap-4"
    >
      {/* DOM order is 1,2,3 (screen-reader reading order); CSS `order-*`
          flips the VISUAL order to #2 · #1 · #3 (UX Spec §6). */}
      <PodiumTile
        row={first}
        place={1}
        Icon={Crown}
        iconColorClass="text-amber-500"
        viewer={first.membership_id === viewerMembershipId}
        className="order-2 -mb-2"
        elevated
      />
      <PodiumTile
        row={second}
        place={2}
        Icon={Medal}
        iconColorClass="text-zinc-400"
        viewer={second.membership_id === viewerMembershipId}
        className="order-1"
      />
      <PodiumTile
        row={third}
        place={3}
        Icon={Medal}
        iconColorClass="text-amber-700"
        viewer={third.membership_id === viewerMembershipId}
        className="order-3"
      />
    </section>
  )
}

interface PodiumTileProps {
  row: CreatorLeaderboardRow
  place: 1 | 2 | 3
  Icon: typeof Crown
  iconColorClass: string
  viewer: boolean
  elevated?: boolean
  className?: string
}

function PodiumTile({
  row,
  place,
  Icon,
  iconColorClass,
  viewer,
  elevated = false,
  className,
}: PodiumTileProps) {
  const { t, i18n } = useTranslation()
  const formattedXp = formatXpNumber(row.weekly_xp, i18n.language)

  return (
    <div
      data-testid={`creator-leaderboard-podium-place-${place}`}
      data-viewer={viewer ? "true" : undefined}
      className={cn(
        "flex flex-col items-center gap-1 text-center",
        elevated ? "w-28" : "w-24",
        viewer && "rounded-2xl",
        className,
      )}
    >
      <Icon
        className={cn(elevated ? "size-6" : "size-5", iconColorClass)}
        aria-hidden="true"
      />
      <MomentumRingAvatar
        src={row.avatar_url}
        name={row.display_name}
        ring={row.progress ? "progress" : "full"}
        progress={row.progress}
        rankPatch={
          row.named_rank
            ? {
                tier: row.tier,
                tierCount: row.tier_count,
                name: row.named_rank,
              }
            : undefined
        }
        size="sm"
        className={cn(
          elevated && "scale-125",
          viewer && "ring-primary rounded-full ring-2 ring-offset-2",
        )}
      />
      <span
        className="mt-1 line-clamp-1 max-w-full text-sm font-semibold"
        title={row.display_name}
      >
        {row.display_name}
      </span>
      {row.named_rank && (
        <span className="text-muted-foreground line-clamp-1 max-w-full text-xs">
          {row.named_rank}
        </span>
      )}
      <span className="text-primary text-sm font-bold tabular-nums">
        {formattedXp} {t("leaderboard.xp_suffix")}
      </span>
      {row.streak_days > 0 && (
        <span
          className="text-muted-foreground flex items-center gap-0.5 text-xs tabular-nums"
          aria-label={t("leaderboard.creator.streak_aria", {
            count: row.streak_days,
          })}
        >
          <Flame className="size-3 text-orange-500" aria-hidden="true" />
          {row.streak_days}
        </span>
      )}
      <span className="sr-only">
        {t(`leaderboard.creator.podium_place_${place}`)}
      </span>
    </div>
  )
}
